import { useState, useEffect, useCallback } from 'react';
import {
  Scale,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Play,
  Download,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import clsx from 'clsx';
import { periodDisplayName, type Period, type TPTransaction, type TPSummary } from '@/types';
import { transferPricingApi, periodsApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Helpers -----------------------------------------------------------------

function formatAmount(amount: number): string {
  if (amount === 0) return '\u2014';
  const abs = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(abs);
  return amount < 0 ? `(${formatted})` : formatted;
}

const PIE_COLORS = ['#1e40af', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#7c3aed', '#a78bfa'];

const complianceConfig: Record<string, { label: string; bg: string; text: string; icon: React.ElementType }> = {
  compliant: { label: 'Compliant', bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle2 },
  non_compliant: { label: 'Non-Compliant', bg: 'bg-red-50', text: 'text-red-700', icon: XCircle },
  pending: { label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock },
};

const riskConfig: Record<string, { bg: string; text: string }> = {
  low: { bg: 'bg-green-100', text: 'text-green-800' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-800' },
  high: { bg: 'bg-red-100', text: 'text-red-800' },
};

// --- Main Page ---------------------------------------------------------------

export default function TransferPricing() {
  const { addNotification } = useAppStore();

  const [isLoading, setIsLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingDocs, setGeneratingDocs] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Period selector
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  // Data
  const [transactions, setTransactions] = useState<TPTransaction[]>([]);
  const [summary, setSummary] = useState<TPSummary | null>(null);

  // Expanded row
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Load periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const res = await periodsApi.list();
        setPeriods(res);
        const openPeriod = res.find((p) => p.status === 'open') || res[0];
        if (openPeriod) setSelectedPeriod(openPeriod.id);
      } catch {
        // silently fail
      }
    };
    loadPeriods();
  }, []);

  // Load TP results
  const loadResults = useCallback(async () => {
    if (!selectedPeriod) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const [txRes, sumRes] = await Promise.all([
        transferPricingApi.results(Number(selectedPeriod)).catch(() => []),
        transferPricingApi.summary(Number(selectedPeriod)).catch(() => null),
      ]);
      setTransactions(txRes);
      setSummary(sumRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load transfer pricing data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (selectedPeriod) loadResults();
  }, [loadResults, selectedPeriod]);

  // Run analysis
  const handleAnalyze = async () => {
    if (!selectedPeriod) return;
    setAnalyzing(true);
    try {
      const txRes = await transferPricingApi.analyze({ period_id: Number(selectedPeriod) });
      setTransactions(txRes);
      // Reload summary
      const sumRes = await transferPricingApi.summary(Number(selectedPeriod)).catch(() => null);
      setSummary(sumRes);
      addNotification({ type: 'success', title: 'Analysis Complete', message: 'Transfer pricing analysis has been completed.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Analysis Failed', message: apiErr.detail || 'Transfer pricing analysis failed.' });
    } finally {
      setAnalyzing(false);
    }
  };

  // Generate documentation
  const handleGenerateDocs = async () => {
    if (!selectedPeriod) return;
    setGeneratingDocs(true);
    try {
      const blob = await transferPricingApi.documentation({ period_id: Number(selectedPeriod) });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `TP_Documentation_${selectedPeriod}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      addNotification({ type: 'success', title: 'Documentation Generated', message: 'TP documentation package has been downloaded.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Generation Failed', message: apiErr.detail || 'Failed to generate documentation.' });
    } finally {
      setGeneratingDocs(false);
    }
  };

  // Build pie chart data from summary
  const methodPieData = summary
    ? Object.entries(summary.by_method).map(([method, count]) => ({
        name: method,
        value: count,
      }))
    : [];

  // Skeleton loading
  if (isLoading && transactions.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <Scale className="w-5 h-5 text-violet-700" />
          </div>
          <div>
            <div className="skeleton w-48 h-7 mb-1" />
            <div className="skeleton w-64 h-4" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="skeleton w-20 h-3 mb-2" />
              <div className="skeleton w-12 h-7" />
            </div>
          ))}
        </div>
        <div className="card p-6 animate-pulse">
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-10" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
            <Scale className="w-5 h-5 text-violet-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Transfer Pricing</h1>
            <p className="text-sm text-gray-500 mt-0.5">Related party transaction analysis and compliance</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="select-field text-sm w-40"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periods.length === 0 && <option value="">No periods</option>}
            {periods.map((p) => (
              <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
            ))}
          </select>
          <button
            onClick={handleAnalyze}
            disabled={analyzing || !selectedPeriod}
            className="btn-primary"
          >
            {analyzing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Run Analysis
          </button>
          <button
            onClick={handleGenerateDocs}
            disabled={generatingDocs || !selectedPeriod || transactions.length === 0}
            className="btn-secondary"
          >
            {generatingDocs ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Generate Docs
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadResults} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* No data state */}
      {!summary && transactions.length === 0 && !error ? (
        <div className="card py-16 text-center">
          <Scale className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">No transfer pricing data</h3>
          <p className="text-sm text-gray-500 mt-1">
            Select a period and click "Run Analysis" to analyze related party transactions.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="card p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">RP Transactions</p>
                <p className="text-2xl font-bold text-gray-900 mt-1 font-mono">{summary.total_transactions}</p>
              </div>
              <div className="card p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Compliant</p>
                <p className="text-2xl font-bold text-green-700 mt-1 font-mono">{summary.compliant}</p>
              </div>
              <div className="card p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Non-Compliant</p>
                <p className="text-2xl font-bold text-red-700 mt-1 font-mono">{summary.non_compliant}</p>
              </div>
              <div className="card p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Adjustment</p>
                <p className={clsx(
                  'text-2xl font-bold mt-1 font-mono',
                  summary.total_adjustment > 0 ? 'text-red-700' : 'text-gray-900',
                )}>
                  {formatAmount(summary.total_adjustment)}
                </p>
              </div>
              <div className="card p-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Risk Rating</p>
                <div className="mt-1">
                  <span className={clsx(
                    'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold uppercase',
                    riskConfig[summary.risk_rating]?.bg || 'bg-gray-100',
                    riskConfig[summary.risk_rating]?.text || 'text-gray-800',
                  )}>
                    {summary.risk_rating === 'high' && <AlertTriangle className="w-4 h-4" />}
                    {summary.risk_rating}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Method Breakdown Chart */}
          {methodPieData.length > 0 && (
            <div className="card p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Transaction Methods Breakdown</h2>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={methodPieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    dataKey="value"
                    nameKey="name"
                    paddingAngle={2}
                    label={({ name, percent }: { name: string; percent: number }) =>
                      `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                  >
                    {methodPieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Transaction Table */}
          {transactions.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
                <h2 className="text-base font-semibold text-gray-900">Related Party Transactions</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="table-header w-8"></th>
                      <th className="table-header">Entity From</th>
                      <th className="table-header">Entity To</th>
                      <th className="table-header text-right w-28">Amount</th>
                      <th className="table-header w-16">CCY</th>
                      <th className="table-header w-24">Method</th>
                      <th className="table-header text-right w-40">Arm&apos;s Length Range</th>
                      <th className="table-header w-28">Status</th>
                      <th className="table-header text-right w-24">Adjustment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => {
                      const isExpanded = expandedId === tx.id;
                      const statusCfg = complianceConfig[tx.compliance_status] || complianceConfig.pending;
                      const StatusIcon = statusCfg.icon;

                      return (
                        <>
                          <tr
                            key={tx.id}
                            className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer transition-colors"
                            onClick={() => setExpandedId(isExpanded ? null : tx.id)}
                          >
                            <td className="table-cell">
                              {isExpanded ? (
                                <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                              )}
                            </td>
                            <td className="table-cell text-sm text-gray-700">{tx.entity_from}</td>
                            <td className="table-cell text-sm text-gray-700">{tx.entity_to}</td>
                            <td className="table-cell text-right font-mono text-sm text-gray-900 font-medium">
                              {formatAmount(tx.amount)}
                            </td>
                            <td className="table-cell text-xs font-mono text-gray-500">{tx.currency}</td>
                            <td className="table-cell">
                              <span className="badge bg-gray-100 text-gray-700 text-xs">{tx.method_applied}</span>
                            </td>
                            <td className="table-cell text-right font-mono text-xs text-gray-500">
                              {formatAmount(tx.arm_length_range.q1)} &ndash; {formatAmount(tx.arm_length_range.q3)}
                            </td>
                            <td className="table-cell">
                              <span className={clsx('badge gap-1', statusCfg.bg, statusCfg.text)}>
                                <StatusIcon className="w-3 h-3" />
                                {statusCfg.label}
                              </span>
                            </td>
                            <td className={clsx(
                              'table-cell text-right font-mono text-sm',
                              tx.adjustment_amount > 0 ? 'text-financial-negative font-semibold' : 'text-gray-600',
                            )}>
                              {formatAmount(tx.adjustment_amount)}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr key={`${tx.id}-detail`} className="bg-gray-50/80">
                              <td colSpan={9} className="px-8 py-5">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                  <div>
                                    <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Method Rationale</h4>
                                    <p className="text-sm text-gray-700">
                                      The {tx.method_applied} method was selected as the most appropriate method
                                      based on the nature of the transaction between {tx.entity_from} and {tx.entity_to}.
                                    </p>
                                  </div>
                                  <div>
                                    <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Arm&apos;s Length Range</h4>
                                    <div className="space-y-1 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Q1 (25th percentile)</span>
                                        <span className="font-mono text-gray-700">{formatAmount(tx.arm_length_range.q1)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Median (50th percentile)</span>
                                        <span className="font-mono text-gray-800 font-semibold">{formatAmount(tx.arm_length_range.median)}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-500">Q3 (75th percentile)</span>
                                        <span className="font-mono text-gray-700">{formatAmount(tx.arm_length_range.q3)}</span>
                                      </div>
                                      <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                                        <span className="text-gray-500">Transaction Amount</span>
                                        <span className="font-mono text-gray-900 font-bold">{formatAmount(tx.amount)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Conclusion</h4>
                                    <p className="text-sm text-gray-700">
                                      {tx.compliance_status === 'compliant'
                                        ? 'The transaction price falls within the interquartile range and is considered at arm\'s length. No adjustment is required.'
                                        : tx.compliance_status === 'non_compliant'
                                        ? `The transaction price of ${formatAmount(tx.amount)} falls outside the arm's length range. An adjustment of ${formatAmount(tx.adjustment_amount)} is recommended to bring the price to the median.`
                                        : 'Analysis is pending. Further review of comparable data is required.'}
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
